import React from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { initializeIcons, setRTL, Spinner, SpinnerSize } from '@fluentui/react';
import AppLauncherPage from 'AppLauncher/AppLauncherPage';
import AuthenticatedAppWithMsal from 'AuthenticatedAppWithMsal';
import AuthenticatedAppWithMsalBroker from 'AuthenticatedAppWithMsalBroker';
import { GlobalToasts } from 'common/components/Notifications/GlobalToast';
import { shouldSetupMsal, shouldSetupMsalBroker } from 'common/utilities/FlightSetting';
import { lazyLoad } from 'common/utilities/LazyLoad';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { WaitForInitialization } from 'common/WaitForInitialization';
import { GetDefenderPage } from 'features/device/AddDevice/GetDefenderPage';
import DigestFeedbackPage from 'features/feedback/DigestFeedbackPage';
import UninstallFeedbackPage from 'features/feedback/UninstallFeedbackPage';
import { i18nInit } from 'i18n';
import { screenSizeChanged, useAppDispatch } from 'store';
import configureAppStore from 'store/configureAppStore';
import UnsubscribePage from 'UnsubcribePage/UnsubscribePage';

import { localeIgnoreCaseEquals } from './common/utilities/String';
import AuthenticatedApp from './AuthenticatedApp';
import { AuthProvider, useAuth } from './AuthProvider';
import { browserHistory, Layout, pageDataService, unknownUser } from './common';
import UnauthenticatedApp from './UnauthenticatedApp';

const Loading = () => <Spinner size={SpinnerSize.large} style={{ flex: 1 }} />;

// Get the application-wide store instance, prepopulating with state from the server where available.
export const store = configureAppStore();

const AppContainer = (): JSX.Element => {
  const { userData } = useAuth();

  if (userData.userId === unknownUser.userId) {
    return <UnauthenticatedApp />;
  }

  // For users who have an active license (or license is not required) and CM auth check is enabled
  if (shouldSetupMsal()) {
    return <AuthenticatedAppWithMsal />;
  }

  // For users who don't have an active license and DIME auth enabled (OTS and paywall)
  if (shouldSetupMsalBroker()) {
    return <AuthenticatedAppWithMsalBroker />;
  }

  return <AuthenticatedApp />;
};

// For unauthenticated pages that need to use React app
// and some redux state, but not all
const CustomAppContainer: React.FC<React.PropsWithChildren> = ({ children }): JSX.Element => {
  const dispatch = useAppDispatch();
  const onResize = React.useCallback((): void => {
    dispatch(screenSizeChanged());
  }, [dispatch]);

  React.useEffect(() => {
    window.addEventListener('resize', onResize);
  }, [onResize]);

  return (
    <WaitForInitialization fallback={<Loading />}>
      <Layout>{children}</Layout>
    </WaitForInitialization>
  );
};

TelemetryProvider.init(browserHistory, !pageDataService.isProdEnvironment());

// Initialize Fluent icons once:
initializeIcons();

// initialize i18n instance
i18nInit();

setRTL(pageDataService.isRtlLocale());

const DigestFeedbackApp = (): JSX.Element => (
  <Provider store={store}>
    <CustomAppContainer>
      <DigestFeedbackPage />
    </CustomAppContainer>
  </Provider>
);

const UninstallFeedbackApp = (): JSX.Element => (
  <Provider store={store}>
    <CustomAppContainer>
      <UninstallFeedbackPage />
    </CustomAppContainer>
  </Provider>
);

const GetDefenderApp = (): JSX.Element => (
  <Provider store={store}>
    <CustomAppContainer>
      <GetDefenderPage />
    </CustomAppContainer>
  </Provider>
);

const AppLauncher = (): JSX.Element => (
  <Provider store={store}>
    <CustomAppContainer>
      <AppLauncherPage />
    </CustomAppContainer>
  </Provider>
);

const UnsubscribeApp = (): JSX.Element => (
  <Provider store={store}>
    <CustomAppContainer>
      <UnsubscribePage />
    </CustomAppContainer>
  </Provider>
);

const AsyncUnauthOneTimeScanPage = lazyLoad(() => import('features/identity/OneTimeScan/UnauthOneTimeScanPage'), { fallback: null });
const UnauthOneTimeScanApp = (): JSX.Element => (
  <AuthProvider // provides support for fetching (polling) license when user proceeds to explore plans
    fetchLicenseOnMount={
      false // since user may not be signed in, yet, and we don't expect user to be licensed when they land on this page
    }
  >
    <Provider store={store}>
      <CustomAppContainer>
        <AsyncUnauthOneTimeScanPage />
        <GlobalToasts />
      </CustomAppContainer>
    </Provider>
  </AuthProvider>
);

const DefaultApp = (): JSX.Element => (
  <AuthProvider>
    <Provider store={store}>
      <Router history={browserHistory}>
        <AppContainer />
      </Router>
    </Provider>
  </AuthProvider>
);

const App = (): JSX.Element => {
  const reactAppName = pageDataService.getReactAppName() ?? '';

  if (localeIgnoreCaseEquals(reactAppName, 'AppLauncher')) {
    return <AppLauncher />;
  }

  if (localeIgnoreCaseEquals(reactAppName, 'Unsubscribe')) {
    return <UnsubscribeApp />;
  }

  if (localeIgnoreCaseEquals(reactAppName, 'DigestFeedback')) {
    return <DigestFeedbackApp />;
  }

  if (localeIgnoreCaseEquals(reactAppName, 'UninstallFeedback')) {
    return <UninstallFeedbackApp />;
  }

  if (localeIgnoreCaseEquals(reactAppName, 'GetDefender')) {
    return <GetDefenderApp />;
  }

  if (localeIgnoreCaseEquals(reactAppName, 'UnauthOneTimeScan')) {
    return <UnauthOneTimeScanApp />;
  }

  return <DefaultApp />;
};

export default App;
